/**
 * People.
 *
 * DATA DEFINITION
 *   A UserId is a String: the lowercased local part of a bharatatomic.com
 *   address, e.g. "jishnu". Interpretation: the stable identity of one person.
 *   We key on the local part rather than the full address so that a future
 *   domain change does not rewrite every provenance record.
 *
 *   A User is a structure { id, name, email, initials, accent }.
 *   Interpretation: a person who may sign in, own changes, and be @-mentioned.
 *   `initials` and `accent` exist so avatars render identically everywhere
 *   without a network round trip — we never fetch a Google profile picture,
 *   which keeps the UI instant and avoids leaking anything to a third party.
 */
import { ok, err } from './result.js';
/** The one Google Workspace domain permitted to sign in. */
export const ALLOWED_DOMAIN = 'bharatatomic.com';
/**
 * Build the two-letter monogram for a display name.
 * "Bhargava Ram Niraghatam" -> "BN"   (first + last, skipping middle names)
 * "Sudipta" -> "SU"
 * initialsFor : String -> String
 */
export function initialsFor(name) {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0)
        return '??';
    if (parts.length === 1)
        return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
/** Construct a User from a name and an email, deriving id/initials. */
function person(name, email, accent) {
    const id = email.split('@')[0].toLowerCase();
    return { id, name, email, initials: initialsFor(name), accent };
}
/**
 * THE ROSTER — the six people who use Lattice.
 *
 * Interpretation: this is an allowlist, not a cache. Sign-in succeeds only for
 * an address in this list. Keeping it in code (rather than a database table)
 * is deliberate: the team is fixed and small, it makes the security boundary
 * reviewable in a diff, and it means the client can render any author's avatar
 * with no lookup.
 */
export const ROSTER = [
    person('Jishnu Dwivedi', 'jishnu@bharatatomic.com', 0),
    person('Prerana Chakrabarti', 'prerana@bharatatomic.com', 1),
    person('Bhargava Ram Niraghatam', 'bhargava@bharatatomic.com', 2),
    person('Sudipta Das', 'sudipta@bharatatomic.com', 3),
    person('Vishnukumar Chaudhary', 'vishnukumar@bharatatomic.com', 4),
    person('Atharva Shukla', 'atharva@bharatatomic.com', 5),
];
const BY_ID = new Map(ROSTER.map(u => [u.id, u]));
const BY_EMAIL = new Map(ROSTER.map(u => [u.email.toLowerCase(), u]));
/** Look up a roster member by id. userById : UserId -> User | undefined */
export const userById = (id) => BY_ID.get(id.toLowerCase());
/** Look up a roster member by email. userByEmail : String -> User | undefined */
export const userByEmail = (email) => BY_EMAIL.get(email.trim().toLowerCase());
/**
 * Decide whether a verified Google identity may use Lattice.
 *
 * This is the single authorisation gate for the whole product. It runs on the
 * server against a Google-verified token; the client never decides this.
 * authorise : String -> Result<User>
 */
export function authorise(verifiedEmail) {
    const email = verifiedEmail.trim().toLowerCase();
    if (!email.includes('@'))
        return err('not an email address');
    if (!email.endsWith(`@${ALLOWED_DOMAIN}`))
        return err(`only @${ALLOWED_DOMAIN} accounts may sign in`);
    const user = userByEmail(email);
    if (!user)
        return err(`${email} is not on the Lattice roster`);
    return ok(user);
}
/**
 * HISTORICAL IDENTITY REMAPPING
 *
 * Interpretation: git attributes a commit to whoever was configured on the
 * workstation, which is not always who did the work. Jishnu Dwivedi worked on
 * Prerana Chakrabarti's laptop, which still had her git identity, so every
 * commit authored as prerana@bharatatomic.com is in fact Jishnu's work.
 *
 * This map applies to IMPORTED history only. Prerana remains a full user of
 * the platform; her future sign-ins and actions are her own.
 */
export const HISTORICAL_AUTHOR_REMAP = {
    'prerana@bharatatomic.com': 'jishnu@bharatatomic.com',
};
/**
 * Resolve the true author of an imported git commit.
 * Falls back to matching on display name for commits whose email is unknown.
 * resolveHistoricalAuthor : String String -> User | undefined
 */
export function resolveHistoricalAuthor(gitEmail, gitName) {
    const email = gitEmail.trim().toLowerCase();
    const remapped = HISTORICAL_AUTHOR_REMAP[email] ?? email;
    const byEmail = userByEmail(remapped);
    if (byEmail)
        return byEmail;
    const needle = gitName.trim().toLowerCase();
    return ROSTER.find(u => u.name.toLowerCase() === needle);
}
//# sourceMappingURL=user.js.map